import { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import { 
  Plus, 
  Search, 
  Filter, 
  MoreHorizontal, 
  Eye, 
  Edit, 
  Trash2,
  Users,
  Calendar,
  MapPin,
  DollarSign,
  Clock,
  BarChart3,
  Share,
  Loader,
  AlertTriangle,
  UserPlus
} from 'lucide-react'
import { apiClient } from '@/lib/api'
import SharePositionModal from '@/components/positions/SharePositionModal'
import SortableTable, { Column } from '@/components/common/SortableTable'

interface Position {
  id: string
  title: string
  department: string
  location: string
  type: 'full-time' | 'part-time' | 'contract'
  status: 'active' | 'paused' | 'filled' | 'closed'
  salaryLabel?: string
  candidates: {
    applied: number
    shortlisted: number
    total: number
    new: number
    interviewed: number
    hired: number
  }
  datePosted: string
  deadline: string
  priority?: 'high' | 'medium' | 'low'
  requirements: string[]
  description: string
  recruiter: string
  recruiterName?: string
  isCreatedByMe?: boolean
  accessReason?: string
  sharedBy?: string
  sharedByName?: string
  sharedTeamName?: string
  shareType?: 'created' | 'shared_individual' | 'shared_team'
}


export default function PositionsPage() {
  const [positions, setPositions] = useState<Position[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table')
  const [showShareModal, setShowShareModal] = useState(false)
  const [selectedPosition, setSelectedPosition] = useState<Position | null>(null)
  const [currentUserId, setCurrentUserId] = useState<string | null>(null)

  useEffect(() => {
    loadCurrentUser()
  }, [])

  useEffect(() => {
    if (currentUserId !== null) {
      loadPositions()
    }
  }, [currentUserId])

  // Listen for position creation events to refresh the list
  useEffect(() => {
    const handlePositionCreated = () => {
      loadPositions() // Refresh the positions list
    }

    window.addEventListener('positionCreated', handlePositionCreated)
    
    return () => {
      window.removeEventListener('positionCreated', handlePositionCreated)
    }
  }, [])

  const loadCurrentUser = async () => {
    try {
      const response = await apiClient.getCurrentUser()
      if (response.success && response.data) {
        setCurrentUserId(response.data.id?.toString() || null)
      }
    } catch (err) {
      console.error('Failed to load current user:', err)
      // Still try to load positions even if we can't get user ID
      setCurrentUserId('')
    }
  }

  const loadPositions = async () => {
    try {
      setLoading(true)
      setError(null)
      
      const response = await apiClient.getPositions({ limit: 500, include_shared: 'true' })

      if (response.success && response.data) {
        const buckets: any[] = []
        const data = response.data as any

        if (Array.isArray(data)) {
          buckets.push(data)
        }

        const possibleKeys = [
          'positions',
          'shared_positions',
          'team_shared_positions',
          'sharedWithMe',
          'shared_with_me',
          'teamShared',
          'team_shared'
        ]

        possibleKeys.forEach(key => {
          const value = data?.[key]
          if (Array.isArray(value)) {
            buckets.push(value)
          }
        })

        const merged = buckets.flat()
        const uniqueById = new Map<string, any>()
        merged.forEach(position => {
          const identifier = (position?.uuid || position?.id)?.toString()
          if (identifier && !uniqueById.has(identifier)) {
            uniqueById.set(identifier, position)
          }
        })

        const positionsData = Array.from(uniqueById.values())

        if (positionsData.length > 0) {
          const visiblePositions = positionsData.filter(position => {
            const rawStatus = (position.status || '').toString().toLowerCase()
            return rawStatus !== 'pending_review' && rawStatus !== 'pending' && rawStatus !== 'rejected'
          })

          const normalizedPositions = visiblePositions.map(position => {
            const salaryLabel = buildSalaryLabel(position)
            const positionRecruiterId = position.recruiter_id?.toString()
            const isCreatedByMe = currentUserId === positionRecruiterId
            const companyName = position.company_name || position.companyName || position.company
            const baseTitle = position.title || 'Untitled Position'
            const displayTitle = companyName ? `${companyName}: ${baseTitle}` : baseTitle
            
            return {
              id: position.id.toString(),
              title: displayTitle,
              companyName,
              description: position.description || 'Job description not available',
              department: position.department_name || position.department || 'General',
              location: position.location || 'Remote',
              type: 'full-time',
              status: position.status === 'open' ? 'active' : position.status,
              salaryLabel,
              candidates: position.candidates || {
                total: 0,
                new: 0,
                interviewed: 0,
                shortlisted: 0,
                hired: 0
              },
              datePosted: position.datePosted || new Date().toISOString().split('T')[0],
              deadline: position.deadline || new Date(Date.now() + 90 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
              priority: position.priority || position.priority_level,
              requirements: [],
              recruiter: position.recruiter_name || 'System',
              recruiterName: position.recruiter_name,
              isCreatedByMe,
              accessReason: position.accessReason || position.access_reason || (isCreatedByMe ? 'Created by you' : 'Shared with you'),
              shareType: position.shareType || position.share_type || (isCreatedByMe ? 'created' : 'shared_individual'),
              sharedByName: position.sharedByName || position.shared_by_name,
              sharedTeamName: position.sharedTeamName || position.shared_team_name
            }
          })
          setPositions(normalizedPositions as Position[])
        } else {
          setPositions([])
        }
      } else {
        setPositions([])
        setError('Failed to load positions')
      }
    } catch (err) {
      console.error('Failed to load positions:', err)
      setPositions([])
      setError('Failed to load positions. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleCreatePosition = async () => {
    // Trigger the parent layout modal
    window.dispatchEvent(new CustomEvent('openCreatePositionModal'))
  }

  const handleViewPosition = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}`
  }

  const handleViewCandidates = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}/candidates`
  }

  const handleViewAnalytics = (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}/analytics`
  }

  const handleEditPosition = async (positionId: string) => {
    window.location.href = `/recruiter/positions/${positionId}`
  }


  const handleCloseShareModal = () => {
    setShowShareModal(false)
    setSelectedPosition(null)
  }

  const handlePositionShared = () => {
    // Optionally reload positions or show success message
    console.log('Position shared successfully')
  }

  const handleSharePosition = (positionId: string) => {
    const position = positions.find(p => p.id === positionId)
    if (position) {
      setSelectedPosition(position)
      setShowShareModal(true)
    }
  }


  const getStatusColor = (status: string) => {
    switch (status) {
      case 'active':
        return 'bg-green-100 text-green-800'
      case 'paused':
        return 'bg-yellow-100 text-yellow-800'
      case 'filled':
        return 'bg-blue-100 text-blue-800'
      case 'closed':
        return 'bg-gray-100 text-gray-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high':
        return 'text-red-600'
      case 'medium':
        return 'text-yellow-600'
      case 'low':
        return 'text-green-600'
      default:
        return 'text-gray-600'
    }
  }

  const buildSalaryLabel = (position: any): string | undefined => {
    if (typeof position.salary_range === 'string' && position.salary_range.trim()) {
      return position.salary_range.trim()
    }

    const min = position.salary_min ?? position.min_salary ?? position.salary?.min
    const max = position.salary_max ?? position.max_salary ?? position.salary?.max
    const currency = position.salary_currency || position.currency || position.salary?.currency

    const formatValue = (value?: number | string) => {
      if (value === undefined || value === null) return null
      const numericValue = typeof value === 'number' ? value : parseFloat(value)
      if (isNaN(numericValue)) return null
      return numericValue.toLocaleString()
    }

    const formattedMin = formatValue(min)
    const formattedMax = formatValue(max)
    if (!formattedMin && !formattedMax) {
      return undefined
    }

    const range = formattedMin && formattedMax
      ? `${formattedMin} - ${formattedMax}`
      : formattedMin || formattedMax

    return currency ? `${currency} ${range}` : range
  }

  const formatSalary = (salaryLabel?: string) => {
    return salaryLabel || 'Salary not disclosed'
  }

  const positionColumns: Column<Position>[] = [
    {
      key: 'title',
      label: 'Position',
      sortable: true,
      filterable: true,
      render: (_, position) => (
        <div>
          <div className="font-medium text-gray-900">{position.title}</div>
          <div className="text-sm text-gray-500 flex items-center">
            <MapPin className="w-3 h-3 mr-1" />
            {position.location}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'active', label: 'Active' },
        { value: 'paused', label: 'Paused' },
        { value: 'filled', label: 'Filled' },
        { value: 'closed', label: 'Closed' }
      ],
      render: (status) => (
        <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(status)}`}>
          {status}
        </span>
      )
    },
    {
      key: 'candidates',
      label: 'Candidates',
      sortable: true,
      render: (candidates) => (
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Applied:</span>
            <span className="font-medium text-blue-600">{candidates?.applied || 0}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Shortlisted:</span>
            <span className="font-medium text-purple-600">{candidates?.shortlisted || 0}</span>
          </div>
        </div>
      )
    },
    {
      key: 'deadline',
      label: 'Deadline',
      sortable: true,
      filterable: true,
      filterType: 'date',
      render: (deadline) => (
        <div className="flex items-center">
          <Calendar className="w-4 h-4 mr-1 text-gray-400" />
          {new Date(deadline).toLocaleDateString()}
        </div>
      )
    },
    {
      key: 'accessReason' as keyof Position,
      label: 'Access',
      sortable: true,
      filterable: true,
      filterType: 'select',
      filterOptions: [
        { value: 'created', label: 'Created by you' },
        { value: 'shared_individual', label: 'Shared individually' },
        { value: 'shared_team', label: 'Shared with team' }
      ],
      render: (_, position) => (
        <div className="flex items-center space-x-2">
          {position.shareType === 'created' ? (
            <div className="flex items-center text-green-600">
              <UserPlus className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Created by you</span>
            </div>
          ) : position.shareType === 'shared_team' ? (
            <div className="flex items-center text-blue-600">
              <Users className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">
                {position.sharedTeamName ? `Team: ${position.sharedTeamName}` : 'Shared with team'}
              </span>
              {position.sharedByName && (
                <span className="text-xs text-gray-500 ml-1">by {position.sharedByName}</span>
              )}
            </div>
          ) : (
            <div className="flex items-center text-purple-600">
              <Share className="w-4 h-4 mr-1" />
              <span className="text-xs font-medium">Shared individually</span>
              {(position.sharedByName || position.recruiterName) && (
                <span className="text-xs text-gray-500 ml-1">by {position.sharedByName || position.recruiterName}</span>
              )}
            </div>
          )}
        </div>
      )
    },
    {
      key: 'actions' as keyof Position,
      label: 'Actions',
      render: (_, position) => (
        <div className="flex items-center space-x-2">
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleViewPosition(position.id)
            }}
            className="p-1 text-gray-400 hover:text-blue-600 transition-colors"
            title="View Position"
          >
            <Eye className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleEditPosition(position.id)
            }}
            className="p-1 text-gray-400 hover:text-green-600 transition-colors"
            title="Edit Position"
          >
            <Edit className="w-4 h-4" />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation()
              handleSharePosition(position.id)
            }}
            className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
            title="Share Position"
          >
            <Share className="w-4 h-4" />
          </button>
        </div>
      )
    },
  ]

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <Loader className="w-8 h-8 animate-spin mx-auto mb-4 text-blue-600" />
          <p className="text-gray-600">Loading positions...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Positions</h1>
            <p className="text-gray-600 mt-1">Manage all open positions and track hiring progress</p>
          </div>
        </div>


        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Error Loading Positions</h3>
            <p className="text-gray-600 mb-4">{error}</p>
            <button onClick={loadPositions} className="btn-primary">
              Try Again
            </button>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Positions</h1>
          <p className="text-gray-600 mt-1">Manage all open positions and track hiring progress</p>
        </div>
        
        <div className="flex items-center space-x-3">
          <button onClick={handleCreatePosition} className="btn-primary">
            <Plus className="w-4 h-4 mr-2" />
            New Position
          </button>
        </div>
      </div>


      {/* Positions List */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="px-6 py-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <h2 className="text-lg font-semibold text-gray-900">
              {(positions || []).length} Positions
            </h2>
            <div className="flex items-center space-x-2">
              <button
                onClick={() => setViewMode('table')}
                className={`p-2 rounded ${viewMode === 'table' ? 'bg-blue-100 text-blue-700' : 'text-gray-400'}`}
              >
                <BarChart3 className="w-4 h-4" />
              </button>
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded ${viewMode === 'grid' ? 'bg-blue-100 text-blue-700' : 'text-gray-400'}`}
              >
                <Users className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>

        {viewMode === 'table' ? (
          <SortableTable
            data={positions}
            columns={positionColumns}
            onRowClick={(position) => handleViewPosition(position.id)}
          />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 p-6">
            {(positions || []).map((position) => (
              <motion.div
                key={position.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900">{position.title}</h3>
                      <p className="text-sm text-gray-500">{position.department}</p>
                    </div>
                    {position.priority && (
                      <div className={`text-xs font-semibold uppercase tracking-wide ${getPriorityColor(position.priority)}`}>
                        {position.priority}
                      </div>
                    )}
                  </div>
                  
                  <div className="mt-4 space-y-2">
                    <div className="flex items-center text-sm text-gray-600">
                      <MapPin className="w-4 h-4 mr-2" />
                      {position.location}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <DollarSign className="w-4 h-4 mr-2" />
                      {formatSalary(position.salaryLabel)}
                    </div>
                    <div className="flex items-center text-sm text-gray-600">
                      <Calendar className="w-4 h-4 mr-2" />
                      Deadline: {new Date(position.deadline).toLocaleDateString()}
                    </div>
                    <div className="flex items-center text-sm mt-2">
                      {position.shareType === 'created' ? (
                        <div className="flex items-center text-green-600">
                          <UserPlus className="w-4 h-4 mr-2" />
                          <span>Created by you</span>
                        </div>
                      ) : position.shareType === 'shared_team' ? (
                        <div className="flex items-center text-blue-600">
                          <Users className="w-4 h-4 mr-2" />
                          <span>
                            {position.sharedTeamName ? `Team: ${position.sharedTeamName}` : 'Shared with team'}
                          </span>
                          {position.sharedByName && <span className="ml-1 text-gray-500">by {position.sharedByName}</span>}
                        </div>
                      ) : (
                        <div className="flex items-center text-purple-600">
                          <Share className="w-4 h-4 mr-2" />
                          <span>Shared individually</span>
                          {(position.sharedByName || position.recruiterName) && <span className="ml-1 text-gray-500">by {position.sharedByName || position.recruiterName}</span>}
                        </div>
                      )}
                    </div>
                  </div>

                  <div className="mt-4">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(position.status)}`}>
                      {position.status}
                    </span>
                  </div>

                  <div className="mt-4 grid grid-cols-2 gap-4 text-center">
                    <div>
                      <div className="text-2xl font-bold text-blue-600">{position.candidates?.applied || 0}</div>
                      <div className="text-xs text-gray-500">Applied</div>
                    </div>
                    <div>
                      <div className="text-2xl font-bold text-purple-600">{position.candidates?.shortlisted || 0}</div>
                      <div className="text-xs text-gray-500">Shortlisted</div>
                    </div>
                  </div>

                  <div className="mt-6 flex items-center justify-between">
                    <button
                      onClick={() => handleViewPosition(position.id)}
                      className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
                    >
                      View Details →
                    </button>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleEditPosition(position.id)
                        }}
                        className="p-1 text-gray-400 hover:text-green-600 transition-colors"
                        title="Edit Position"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={(e) => {
                          e.stopPropagation()
                          handleSharePosition(position.id)
                        }}
                        className="p-1 text-gray-400 hover:text-purple-600 transition-colors"
                        title="Share Position"
                      >
                        <Share className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        )}

        {(positions || []).length === 0 && (
          <div className="text-center py-8">
            <Users className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No positions found</h3>
            <p className="text-gray-600 mb-6">Start by creating your first position.</p>
            <button onClick={handleCreatePosition} className="btn-primary">
              <Plus className="w-4 h-4 mr-2" />
              Create First Position
            </button>
          </div>
        )}
      </div>


      {/* Share Position Modal */}
      <SharePositionModal
        open={showShareModal}
        onClose={handleCloseShareModal}
        position={selectedPosition}
        onShared={handlePositionShared}
      />
    </div>
  )
}
